import XLSX, {
  CellStyle,
  BorderType,
  CellStyleColor,
  CellObject,
  RowInfo,
  Range,
  WorkSheet,
  ColInfo,
} from 'xlsx-js-style';
import { ExcelDataType } from 'xlsx';
import { downloadBlob } from './element';
import html2canvas from 'html2canvas'
import ApexCharts from 'apexcharts';

const borderStyle: { color: CellStyleColor; style?: BorderType } = {
  color: { rgb: '0c0c0c' },
  style: 'medium',
};

const fullBorderStyle: CellStyle['border'] = {
  top: borderStyle,
  bottom: borderStyle,
  left: borderStyle,
  right: borderStyle,
};

const headerStyle: CellStyle = {
  fill: {
    fgColor: {
      rgb: 'efefef',
    },
  },
  font: {
    color: { rgb: '3f4d67' },
  },
  border: fullBorderStyle,
};
const oddRowStyle: CellStyle = {
  fill: { fgColor: { rgb: 'a6f0ff' } },
};
const cellStyle: CellStyle = {
  border: fullBorderStyle,
};

export type SheetHeaderDisplay<
  T extends Record<string, unknown>,
  K extends keyof T = keyof T
> = {
  display: string;
  property: K;
  sheetStyle?: CellStyle;
  onSheetRender?: (
    value: T[K],
    idx: number,
    context: {
      context: Record<string, unknown>;
      allValues: T;
    }
  ) => string | number | Date;
};

type Props<T extends Record<string, unknown>> = {
  title: string;
  values: T[];
  headers: SheetHeaderDisplay<T>[];
  merge?: Range[];
  context?: Record<string, unknown>;
  groupBy?: (v: T) => string;
  footer?: string;
  orientation?: 'portrait' | 'landscape';
};

export function downloadDataAsExcel<T extends Record<string, unknown>>({
  title,
  values,
  headers,
  merge = [],
  context = {},
  groupBy = () => title,
  footer,
  exportfile,
}: Props<T> & { exportfile?: number }): void {
  const MAX_COL_WCH = 30;
  const MIN_COL_WCH = 6;
  const LINE_HPT = 15;
  const MIN_ROW_HPT = 18;
  const MAX_ROW_HPT = 120;

  const isODS = exportfile === 1;

  const getSafeSheetName = (name: string): string => {
    let sheetName = name;

    if (sheetName.length > 31) {
      const underscoreIndex = sheetName.lastIndexOf('_');

      if (underscoreIndex !== -1 && underscoreIndex < sheetName.length - 1) {
        sheetName = sheetName.substring(underscoreIndex + 1);
      }
    }

    // Excel Sheet 名稱不能包含這些字元
    sheetName = sheetName.replace(/[\\/?*[\]:]/g, '');

    // "_" 後面的文字本身如果還是超過 31 字
    if (sheetName.length > 31) {
      sheetName = sheetName.substring(0, 31);
    }

    return sheetName || 'Sheet';
  };

  // =========================
  // 計算文字顯示寬度
  // 中文 / 全形 = 2
  // 英文 / 數字 = 1
  // =========================
  const stringWch = (val: unknown): number => {
    if (val == null) return 0;

    const s = String(val);
    let w = 0;

    for (let i = 0; i < s.length; i++) {
      const code = s.charCodeAt(i);

      if (
        (code >= 0x1100 && code <= 0x11ff) ||
        (code >= 0x2e80 && code <= 0x9fff) ||
        (code >= 0xac00 && code <= 0xd7af) ||
        (code >= 0xff00 && code <= 0xffef)
      ) {
        w += 2;
      } else {
        w += 1;
      }
    }

    return w;
  };

  // =========================
  // A1 欄位字母
  // =========================
  const colIdxToA1 = (idx0: number) => {
    let n = idx0 + 1;
    let s = '';

    while (n > 0) {
      const r = (n - 1) % 26;
      s = String.fromCharCode(65 + r) + s;
      n = Math.floor((n - 1) / 26);
    }

    return s;
  };

  // =========================
  // 取得 cell 真正的值
  // =========================
  const getCellValue = (cell: any) => {
    if (
      cell &&
      typeof cell === 'object' &&
      'v' in cell
    ) {
      return cell.v;
    }

    return cell;
  };

  // =========================
  // 計算欄寬
  // =========================
  const buildColWidths = (
    rows: any[][],
    colCount: number,
    opts: {
      min?: number;
      max?: number;
      pad?: number;
    } = {}
  ) => {
    const {
      min = MIN_COL_WCH,
      max = MAX_COL_WCH,
      pad = 2,
    } = opts;

    const widths = Array(colCount).fill(min);

    for (let r = 0; r < rows.length; r++) {
      const row = rows[r] || [];

      for (let c = 0; c < colCount; c++) {
        const value = getCellValue(row[c]);

        // 如果文字本身有換行，
        // 用最長的那一行計算欄寬
        const text = value == null
          ? ''
          : String(value);

        const lines = text.split(/\r?\n/);

        const longestLineWidth = Math.max(
          ...lines.map((line) => stringWch(line)),
          0
        );

        const width = longestLineWidth + pad;

        if (width > widths[c]) {
          widths[c] = width;
        }
      }
    }

    return widths.map((w) => ({
      wch: Math.min(
        Math.max(w, min),
        max
      ),
    }));
  };

  // =========================
  // 根據「實際欄寬」計算列高
  //
  // 原本最大的問題就是：
  // 每一欄都拿 WRAP_AT_WCH = 50 算。
  //
  // 現在改成依每欄實際寬度計算。
  // =========================
  const buildRowHeights = (
    rows: any[][],
    colWidths: { wch?: number }[],
    noWrapRowIndex: number | null = null
  ): { hpt: number }[] => {
    const heights: { hpt: number }[] = [];

    for (let r = 0; r < rows.length; r++) {
      if (
        noWrapRowIndex !== null &&
        r === noWrapRowIndex
      ) {
        heights.push({
          hpt: MIN_ROW_HPT,
        });

        continue;
      }

      const row = rows[r] || [];

      let maxLines = 1;

      for (let c = 0; c < row.length; c++) {
        const value =
          getCellValue(row[c]);

        if (
          value === undefined ||
          value === null ||
          value === ''
        ) {
          continue;
        }

        const text = String(value);

        const colWidth = Math.max(
          colWidths[c]?.wch ?? 10,
          1
        );

        // Excel 實際可用空間會比設定欄寬稍小，
        // 所以乘 0.9 留一點安全空間。
        const usableWidth = Math.max(
          colWidth * 0.9,
          1
        );

        const paragraphs =
          text.split(/\r?\n/);

        let lines = 0;

        paragraphs.forEach((paragraph) => {
          const width =
            stringWch(paragraph);

          lines += Math.max(
            1,
            Math.ceil(
              width / usableWidth
            )
          );
        });

        maxLines = Math.max(
          maxLines,
          lines
        );
      }

      const height = Math.min(
        Math.max(
          MIN_ROW_HPT,
          maxLines * LINE_HPT
        ),
        MAX_ROW_HPT
      );

      heights.push({
        hpt: height,
      });
    }

    return heights;
  };

  // =========================
  // Excel 列印設定
  // =========================
  const applyPrintSettings = (
    sheet: any,
    colCount: number,
    rowCount: number
  ) => {
    if (isODS) return;

    /*
     * 欄位較多時使用橫式。
     * 6 欄以下使用直式。
     */
    const landscape =
      colCount > 6;

    sheet['!margins'] = {
      left: 0.25,
      right: 0.25,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3,
    };

    /*
     * xlsx-js-style / SheetJS 的列印資訊
     */
    sheet['!pageSetup'] = {
      paperSize: 9,

      orientation: landscape
        ? 'landscape'
        : 'portrait',

      fitToPage: true,

      // 最重要：
      // 所有欄位強制放在一頁寬度
      fitToWidth: 1,

      // 高度不限
      // 學生多可以自然往第 2、3、4 頁
      fitToHeight: 0,

      horizontalCentered: true,
      verticalCentered: false,
    };

    /*
     * 列印範圍
     */
    sheet['!printArea'] =
      `A1:${colIdxToA1(
        colCount - 1
      )}${rowCount}`;

    /*
     * 前面：
     * 1 = title
     * 2 = group title
     * 3 = 空白
     * 4 = table header
     *
     * 希望後續頁面重複第 4 列表頭。
     */
    sheet['!printTitles'] = {
      rows: '4:4',
    };
  };

  // =========================
  // ODS 相容
  // =========================
  const EMPTY = () => ({
    v: '',
    t: 's' as const,
  });

  const makeCell = (
    v: any,
    style?: any
  ) =>
    isODS
      ? {
        v,
        t: 's' as const,
      }
      : {
        v,
        t: 's' as const,
        s: style,
      };

  const fillRowTo = (
    arr: any[],
    colCount: number
  ) => {
    const row = [...arr];

    while (row.length < colCount) {
      row.push(EMPTY());
    }

    return row.slice(
      0,
      colCount
    );
  };

  // =========================
  // 表頭
  // =========================
  const headerArray: CellObject[] =
    headers.map((h) =>
      makeCell(
        h.display,
        {
          ...h.sheetStyle,
          ...headerStyle,

          alignment: {
            ...(h.sheetStyle?.alignment ||
              {}),

            wrapText: true,
            vertical: 'center',
            horizontal: 'center',
          },
        }
      )
    );

  // =========================
  // 分組
  // =========================
  const grouped = values.reduce(
    (pv, v) => {
      const key = groupBy(v);

      pv[key] = [
        ...(pv[key] || []),
        v,
      ];

      return pv;
    },
    {} as Record<string, T[]>
  );

  const book =
    XLSX.utils.book_new();

  // =========================
  // 索引頁
  // =========================
  const indexCols = 1;

  const indexSheetData: CellObject[][] =
    [
      [
        makeCell(
          `${title} - 分頁索引`,
          {
            font: {
              bold: true,
              sz: 14,
            },

            alignment: {
              wrapText: true,
              vertical: 'center',
              horizontal: 'left',
            },
          }
        ),
      ],

      [
        EMPTY(),
      ],
    ];

  const tabNames = Object.keys(grouped);

  const sheetNameMap = new Map<string, string>();

  tabNames.forEach((tab) => {
    sheetNameMap.set(tab, getSafeSheetName(tab));
  });

  tabNames.forEach((name) => {
    const sheetName =
      sheetNameMap.get(name) ?? getSafeSheetName(name);

    indexSheetData.push([
      isODS
        ? ({
          v: sheetName,
          t: 's',
          l: {
            Target: `#'${sheetName}'!A1`,
          },
        } as any)
        : ({
          f: `HYPERLINK("#'${sheetName}'!A1", "${sheetName}")`,
          t: 's',
          s: {
            font: {
              color: {
                rgb: '0000FF',
              },
              underline: true,
            },
            alignment: {
              wrapText: true,
              vertical: 'center',
              horizontal: 'left',
            },
          },
        } as any),
    ]);
  });

  const indexRowsFinal =
    isODS
      ? indexSheetData.map(
        (r) =>
          fillRowTo(
            r,
            indexCols
          )
      )
      : indexSheetData;

  const indexSheet =
    XLSX.utils.aoa_to_sheet(
      indexRowsFinal,
      {
        sheetStubs: !isODS,
        cellStyles: !isODS,
      }
    );

  const indexColWidths =
    buildColWidths(
      indexRowsFinal,
      indexCols,
      {
        min: 10,
        max: 60,
        pad: 2,
      }
    );

  (indexSheet as any)['!cols'] =
    indexColWidths;

  if (!isODS) {
    (indexSheet as any)['!rows'] =
      buildRowHeights(
        indexRowsFinal,
        indexColWidths
      );
  }

  if (isODS) {
    const lastRow =
      indexRowsFinal.length;

    (indexSheet as any)['!ref'] =
      `A1:${colIdxToA1(
        indexCols - 1
      )}${lastRow}`;
  }

  XLSX.utils.book_append_sheet(
    book,
    indexSheet,
    '索引頁'
  );

  // =========================
  // 每個群組分頁
  // =========================
  tabNames.forEach((tab) => {
    const datas =
      grouped[tab];

    const rows: CellObject[][] =
      datas.map((v, idx) =>
        headers.map((h) => {
          const rawValue =
            h.onSheetRender
              ? h.onSheetRender(
                v[h.property],
                idx,
                {
                  allValues: v,
                  context,
                }
              )
              : v[h.property];

          /*
           * 不使用 || ''
           *
           * 因為數字 0 也會被 || 判斷成 false，
           * 導致 0 變成空字串。
           */
          const value =
            rawValue ?? '';

          return makeCell(
            value,
            {
              ...h.sheetStyle,
              ...cellStyle,

              ...(idx % 2
                ? oddRowStyle
                : {}),

              alignment: {
                ...(h.sheetStyle
                  ?.alignment || {}),

                wrapText: true,

                vertical:
                  'center',

                horizontal:
                  'left',
              },
            }
          );
        })
      );

    const cols = headerArray.length;

    const sep = [EMPTY(),];

    const fullRowsRaw:
      CellObject[][] = [
        // Row 1
        [
          makeCell(
            title,
            {
              alignment: {
                wrapText: true,
                vertical: 'center',
                horizontal: 'left',
              },

              font: {
                bold: true,
                sz: 14,
              },
            }
          ),
        ],

        // Row 2
        [
          makeCell(
            tab,
            {
              alignment: {
                wrapText: true,
                vertical: 'center',
                horizontal: 'left',
              },

              font: {
                bold: true,
                sz: 12,
              },
            }
          ),
        ],

        // Row 3
        sep,

        // Row 4
        headerArray,

        // Row 5...
        ...rows,

        sep,

        footer
          ? [
            makeCell(
              footer,
              {
                alignment: {
                  wrapText: false,
                  vertical: 'center',
                  horizontal: 'left',
                },
              }
            ),
          ]
          : [],
      ];

    const fullRows =
      isODS
        ? fullRowsRaw.map(
          (r) =>
            fillRowTo(
              r,
              cols
            )
        )
        : fullRowsRaw;

    const sheet =
      XLSX.utils.aoa_to_sheet(
        fullRows,
        {
          sheetStubs: !isODS,
          cellStyles: !isODS,
        }
      );

    // =========================
    // 欄寬
    //
    // 標題/footer 不參與，
    // 避免一大串 footer 把 A 欄撐超寬。
    // =========================
    const widthRowsForCols = [
      headerArray,
      ...rows,
    ];

    const colWidths =
      buildColWidths(
        widthRowsForCols,
        cols,
        {
          min: MIN_COL_WCH,
          max: MAX_COL_WCH,
          pad: 2,
        }
      );

    (sheet as any)['!cols'] =
      colWidths;

    // =========================
    // 列高
    // =========================
    if (!isODS) {
      const noWrapRowIndex =
        footer
          ? fullRows.length - 1
          : null;

      (sheet as any)['!rows'] =
        buildRowHeights(
          fullRows,
          colWidths,
          noWrapRowIndex
        );
    }

    // =========================
    // Merge
    // =========================
    (sheet as any)['!merges'] = [
      {
        s: {
          c: 0,
          r: 0,
        },

        e: {
          c: cols - 1,
          r: 0,
        },
      },

      {
        s: {
          c: 0,
          r: 1,
        },

        e: {
          c: cols - 1,
          r: 1,
        },
      },

      ...merge,
    ];

    // =========================
    // ODS ref
    // =========================
    if (isODS) {
      const lastRow =
        fullRows.length;

      (sheet as any)['!ref'] =
        `A1:${colIdxToA1(
          cols - 1
        )}${lastRow}`;
    }

    // =========================
    // Excel 列印設定
    // =========================
    applyPrintSettings(
      sheet,
      cols,
      fullRows.length
    );

    const sheetName =
      sheetNameMap.get(tab) ?? getSafeSheetName(tab);

    XLSX.utils.book_append_sheet(
      book,
      sheet,
      sheetName
    );
  });

  // =========================
  // 輸出
  // =========================
  const bookType =
    isODS
      ? 'ods'
      : 'xlsx';

  const mime =
    isODS
      ? 'application/vnd.oasis.opendocument.spreadsheet'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const ext =
    isODS
      ? 'ods'
      : 'xlsx';

  const buffer =
    XLSX.write(
      book,
      {
        type: 'array',
        bookType,

        cellStyles:
          !isODS,
      }
    );

  const blob =
    new Blob(
      [buffer],
      {
        type: mime,
      }
    );

  downloadBlob(
    blob,
    `${title}.${ext}`
  );
}

export function printDataAsTable<T extends Record<string, unknown>>({
  title,
  values,
  headers,
  context = {},
  groupBy = () => title,
  footer,
  orientation = 'landscape',
}: Props<T>): void {
  if (!values.length) return;

  const isPortrait = orientation === 'portrait';

  // A4 扣除上下左右各 10mm 邊界後的可用尺寸
  const pageWidthMm = isPortrait ? 190 : 277;
  const pageHeightMm = isPortrait ? 277 : 190;

  const escapeHtml = (value: unknown): string => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const grouped = values.reduce((result, value) => {
    const key = groupBy(value);

    if (!result[key]) {
      result[key] = [];
    }

    result[key].push(value);
    return result;
  }, {} as Record<string, T[]>);

  const groupsHtml = Object.entries(grouped)
    .map(([groupName, datas], groupIndex) => {
      const headerHtml = headers
        .map((header) => {
          return `<th>${escapeHtml(header.display)}</th>`;
        })
        .join('');

      const rowsHtml = datas
        .map((data, rowIndex) => {
          const cellsHtml = headers
            .map((header) => {
              const originalValue = data[header.property];

              const renderedValue = header.onSheetRender
                ? header.onSheetRender(originalValue, rowIndex, {
                  allValues: data,
                  context,
                })
                : originalValue;

              return `<td>${escapeHtml(renderedValue)}</td>`;
            })
            .join('');

          return `
            <tr data-row-index="${rowIndex}">
              ${cellsHtml}
            </tr>
          `;
        })
        .join('');

      return `
        <div
          class="source-group"
          data-group-index="${groupIndex}"
          data-group-name="${escapeHtml(groupName)}"
        >
          <div class="source-heading">
            <h1>${escapeHtml(title)}</h1>

            ${groupName !== title
          ? `<h2>${escapeHtml(groupName)}</h2>`
          : ''
        }
          </div>

          <table>
            <thead>
              <tr>${headerHtml}</tr>
            </thead>

            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>
      `;
    })
    .join('');

  const footerHtml = footer
    ? `
      <div class="footer">
        <span>承辦人:</span>
        <span>組長:</span>
        <span>主任:</span>
        <span>校長:</span>
      </div>
    `
    : '';

  const printWindow = window.open(
    '',
    '_blank',
    'width=1200,height=800'
  );

  if (!printWindow) {
    alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
    return;
  }

  printWindow.document.open();

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="zh-Hant">
      <head>
        <meta charset="UTF-8" />

        <title>${escapeHtml(title)}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            color: #000;
            font-family:
              "Microsoft JhengHei",
              "Noto Sans TC",
              Arial,
              sans-serif;
          }

          body {
            background: #fff;
          }

          .print-page {
            position: relative;
            width: 100%;
            height: ${pageHeightMm}mm;
            min-height: ${pageHeightMm}mm;
            max-height: ${pageHeightMm}mm;
            padding-bottom: 16mm;
            overflow: hidden;

            page-break-after: always;
            break-after: page;
          }

          .print-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .page-content {
            width: 100%;
          }

          h1 {
            margin: 0 0 6px;
            text-align: center;
            font-size: 18px;
            line-height: 1.3;
          }

          h2 {
            margin: 0 0 6px;
            text-align: center;
            font-size: 14px;
            line-height: 1.3;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th,
          td {
            border: 1px solid #000;
            padding: 4px 3px;
            text-align: center;
            vertical-align: middle;
            font-size: 10px;
            line-height: 1.25;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          th {
            background: #f2f2f2;
            font-weight: bold;
          }

          .footer {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            width: 100%;
            padding: 0 6mm;
            font-size: 12px;
          }

          .footer span {
            width: 25%;
            text-align: left;
          }

          #source-root {
            position: absolute;
            left: -99999px;
            top: 0;
            width: ${pageWidthMm}mm;
            visibility: hidden;
          }

          #print-root {
            width: 100%;
          }

          @page {
            size: A4 ${orientation};
            margin: 10mm;
          }

          @media screen {
            body {
              padding: 20px;
              background: #eee;
            }

            #print-root {
              width: ${pageWidthMm}mm;
              margin: 0 auto;
            }

            .print-page {
              width: ${pageWidthMm}mm;
              height: ${pageHeightMm}mm;
              min-height: ${pageHeightMm}mm;
              max-height: ${pageHeightMm}mm;
              margin-bottom: 20px;
              padding: 8mm 8mm 18mm;
              background: #fff;
              box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
            }

            .footer {
              left: 8mm;
              right: 8mm;
              bottom: 6mm;
              width: auto;
              padding: 0;
            }
          }

          @media print {
            html,
            body {
              width: 100%;
              margin: 0;
              padding: 0;
              background: #fff;
            }

            #print-root {
              width: 100%;
            }

            .print-page {
              width: 100%;
              height: ${pageHeightMm}mm;
              min-height: ${pageHeightMm}mm;
              max-height: ${pageHeightMm}mm;
              margin: 0;
              padding: 0 0 16mm;
            }

            .footer {
              left: 0;
              right: 0;
              bottom: 0;
            }

            tr,
            th,
            td {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }
        </style>
      </head>

      <body>
        <div id="source-root">
          ${groupsHtml}
        </div>

        <div id="print-root"></div>

        <script>
          function mmToPx(mm) {
            const element = document.createElement('div');

            element.style.position = 'absolute';
            element.style.visibility = 'hidden';
            element.style.width = '1px';
            element.style.height = mm + 'mm';

            document.body.appendChild(element);

            const height =
              element.getBoundingClientRect().height;

            element.remove();

            return height;
          }

          function createPage(groupName, isContinuation) {
            const page = document.createElement('section');
            page.className = 'print-page';

            const content = document.createElement('div');
            content.className = 'page-content';

            const heading = document.createElement('div');
            heading.className = 'page-heading';

            const titleElement =
              document.createElement('h1');

            titleElement.textContent =
              ${JSON.stringify(title)};

            heading.appendChild(titleElement);

            if (
              groupName &&
              groupName !== ${JSON.stringify(title)}
            ) {
              const groupElement =
                document.createElement('h2');

              groupElement.textContent =
                groupName +
                (isContinuation ? '（續）' : '');

              heading.appendChild(groupElement);
            }

            const table =
              document.createElement('table');

            const thead =
              document.createElement('thead');

            thead.innerHTML = ${JSON.stringify(`
              <tr>
                ${headers
      .map(
        (header) =>
          `<th>${escapeHtml(header.display)}</th>`
      )
      .join('')}
              </tr>
            `)};

            const tbody =
              document.createElement('tbody');

            table.appendChild(thead);
            table.appendChild(tbody);

            content.appendChild(heading);
            content.appendChild(table);
            page.appendChild(content);

            return {
              page,
              content,
              tbody,
            };
          }

          function paginateGroups() {
            const sourceRoot =
              document.getElementById('source-root');

            const printRoot =
              document.getElementById('print-root');

            if (!sourceRoot || !printRoot) return;

            printRoot.innerHTML = '';

            const pageHeight =
              mmToPx(${pageHeightMm});

            const footerReserveHeight =
              mmToPx(16);

            const availableContentHeight =
              pageHeight - footerReserveHeight;

            const groups = Array.from(
              sourceRoot.querySelectorAll('.source-group')
            );

            groups.forEach(function (group) {
              const groupName =
                group.getAttribute('data-group-name') || '';

              const sourceRows = Array.from(
                group.querySelectorAll('tbody tr')
              );

              const groupPages = [];

              let pageData = createPage(
                groupName,
                false
              );

              printRoot.appendChild(pageData.page);
              groupPages.push(pageData);

              sourceRows.forEach(function (sourceRow) {
                const clonedRow =
                  sourceRow.cloneNode(true);

                pageData.tbody.appendChild(clonedRow);

                const currentHeight =
                  pageData.content
                    .getBoundingClientRect()
                    .height;

                if (
                  currentHeight >
                    availableContentHeight &&
                  pageData.tbody.children.length > 1
                ) {
                  pageData.tbody.removeChild(clonedRow);

                  pageData = createPage(
                    groupName,
                    true
                  );

                  printRoot.appendChild(pageData.page);
                  groupPages.push(pageData);

                  pageData.tbody.appendChild(
                    sourceRow.cloneNode(true)
                  );
                }
              });

              const lastPageData =
                groupPages[groupPages.length - 1];

              if (lastPageData) {
                lastPageData.page.insertAdjacentHTML(
                  'beforeend',
                  ${JSON.stringify(footerHtml)}
                );
              }
            });
          }

          window.onload = function () {
            window.focus();

            setTimeout(function () {
              paginateGroups();

              setTimeout(function () {
                window.print();
              }, 500);
            }, 300);
          };

          window.onbeforeprint = function () {
            paginateGroups();
          };

          window.onafterprint = function () {
            window.close();
          };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
}

export function downloadDataAsPDFByPrint<T extends Record<string, unknown>>({
  title,
  values,
  headers,
  groupBy = () => title,
  footer,
  context = {},
}: Props<T>) {

  const grouped = values.reduce((pv, v) => {
    const key = groupBy(v);
    pv[key] = [...(pv[key] || []), v];
    return pv;
  }, {} as Record<string, T[]>);

  const win = window.open('', '_blank');
  if (!win) return;

  win.document.write(`
  <html>
  <head>
    <title>${title}</title>
    <style>
      body { font-family: "Microsoft JhengHei", "Noto Sans TC", sans-serif; }
      .page { page-break-after: always; padding: 20px; }
      h1 { font-size: 18px; }
      h2 { font-size: 14px; margin-bottom: 10px; }
      table { border-collapse: collapse; width: 100%; font-size: 12px; }
      th, td { border: 1px solid #000; padding: 4px; }
      th { background: #eee; }
      .footer {
  margin-top: 20px;
  padding: 0 8mm;
  display: flex;
  width: 100%;
  font-size: 12px;
}

.footer span {
  width: 25%;
  text-align: left;
}
    </style>
  </head>
  <body>
  `);

  Object.entries(grouped).forEach(([sheetName, datas]) => {
    win.document.write(`<div class="page">`);
    win.document.write(`<h1>${title}</h1>`);
    win.document.write(`<h2>${sheetName}</h2>`);
    win.document.write(`<table><thead><tr>`);

    headers.forEach(h => {
      win.document.write(`<th>${h.display}</th>`);
    });

    win.document.write(`</tr></thead><tbody>`);

    datas.forEach((row, idx) => {
      win.document.write(`<tr>`);
      headers.forEach(h => {
        const value = h.onSheetRender
          ? h.onSheetRender(row[h.property], idx, { allValues: row, context })
          : row[h.property];
        win.document.write(`<td>${value ?? ''}</td>`);
      });
      win.document.write(`</tr>`);
    });

    win.document.write(`</tbody></table>`);

    if (footer) {
      win.document.write(`<div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>`);
    }

    win.document.write(`</div>`);
  });

  win.document.write(`</body></html>`);
  win.document.close();
  win.focus();
  win.print();   // 使用瀏覽器另存成 PDF
}

export function downloadDataAsPDFByPrintFromTable(
  title: string,
  footer?: string
) {
  const tableHtml = document.getElementById('print-table')?.outerHTML;
  if (!tableHtml) return;

  const win = window.open('', '_blank');
  if (!win) return;

  win.document.write(`
    <html>
    <head>
  <title>${title}</title>
  <style>
   @page {
  size: A4 portrait;
  margin: 6mm;
}

html, body {
  width: 100%;
  height: 100%;
  margin: 0;
}

body {
  font-family: "Microsoft JhengHei", "Noto Sans TC", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;      /* 表格水平置中 */
}

.title {
  font-size: 16px;          /* 標題縮小 */
  font-weight: bold;
  margin: 4px 0 6px 0;
  text-align: center;
}

table {
  width: 100%;
  max-width: 190mm;        /* 限制在A4可列印寬度 */
  border-collapse: collapse;
  table-layout: fixed;
}

th, td {
  border: 1px solid #000;
  padding: 2px;
  text-align: center;
  font-size: 9px;
  line-height: 1.1;
}

.footer {
  width: 100%;
  max-width: 190mm;
  margin-top: 6px;
  font-size: 10px;
  display: flex;
  justify-content: space-between;
}

.footer span {
  min-width: 45mm;   /* 每個簽名欄寬度 */
}
  </style>
</head>
   <body>
  <div class="title">${title}</div>
  ${tableHtml}
  <div class="footer">
  <span>承辦人：</span>
  <span>組長：</span>
  <span>主任：</span>
  <span>校長：</span>
</div>
</body>
    </html>
  `);

  win.document.close();
  win.focus();
  win.print();
}

type Orientation = 'portrait' | 'landscape';

type PrintArgument = {
  title: string;
  tableElementId: string;
  charts: { id: string; title: string }[];

  firstPageOrientation?: Orientation;

  chartPageOrientation?: Orientation;

  tableAsImage?: boolean;

  tableImageScale?: number;
};

export async function downloadSheetChartAsPDFByPrint({
  title,
  tableElementId,
  charts,
  firstPageOrientation = 'landscape',
  chartPageOrientation = 'landscape',
  tableAsImage = true,
  tableImageScale = 2
}: PrintArgument) {
  const table = document.getElementById(tableElementId);
  if (!table) {
    alert('找不到表格元素');
    return;
  }

  const win = window.open('', '_blank');
  if (!win) return;

  const chartPagesHtml = charts
    .map(
      (c, i) => `
      <div class="page ${chartPageOrientation}">
        <div class="page-head">
          <div class="h2">${c.title}</div>
        </div>
        <div id="print-chart-${i}" class="chart-box"></div>
      </div>
    `
    )
    .join('');

  win.document.write(`
<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8" />
<title>${title}</title>
<style>
/* 兩種 page size */
@page portrait { size: A4 portrait; margin: 8mm; }
@page landscape { size: A4 landscape; margin: 6mm; }

.page { 
  page-break-after: always;
  break-after: page;
  width: 100%;
  font-family: "Noto Sans TC","Microsoft JhengHei", Arial;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.page.portrait { page: portrait; }
.page.landscape { page: landscape; }

.page.layout {
  display: flex;
  flex-direction: column;
}

.page-head{
  text-align: center;
  margin: 0 0 2mm 0;
}
.h1{ font-size: 16px; font-weight: 700; margin: 0; }
.h2{ font-size: 14px; font-weight: 700; margin: 0; }

.page-body{
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden; /* 避免溢出導致被推頁 */
}
.chart-box {
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart-img {
  max-width: 95%;
  max-height: 180mm;
  object-fit: contain;
}
.table-img{
  display: block;
  margin: 0 auto;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  break-inside: avoid;
}

.page.landscape .table-img { max-height: 170mm; }
.page.portrait  .table-img { max-height: 245mm; }

.footer {
  margin-top: 4mm;
  font-size: 11px;
  display: flex;
  justify-content: space-between;
}

.footer span {
  min-width: 45mm;
}
</style>
</head>
<body>

<div class="page layout ${firstPageOrientation}">
  <div class="page-head">
    <div class="h1">${title}</div>
  </div>

  <div class="page-body">
    ${tableAsImage
      ? `<img id="print-table-img" class="table-img" />`
      : `<div class="table-wrapper">${table.outerHTML}</div>`}
  </div>

   <div class="footer">
    <span>承辦人：</span>
    <span>組長：</span>
    <span>主任：</span>
    <span>校長：</span>
  </div>
</div>
</div>

${chartPagesHtml}

</body>
</html>
  `);

  win.document.close();

  // 1) 表格轉圖（可選）
  if (tableAsImage) {
    const { default: html2canvas } = await import('html2canvas');

    const canvas = await html2canvas(table, {
      scale: tableImageScale,
      backgroundColor: '#ffffff',
      useCORS: true
    });

    const imgEl = win.document.getElementById('print-table-img') as HTMLImageElement;
    imgEl.src = canvas.toDataURL('image/png');

    await new Promise<void>((resolve) => {
      imgEl.onload = () => resolve();
    });
  }

  // 2) 把 ApexChart 轉成圖片塞進新視窗
  const chartImgs: HTMLImageElement[] = [];

  for (let i = 0; i < charts.length; i++) {
    const result = await ApexCharts.exec(charts[i].id, 'dataURI');

    if (!result) {
      console.warn(`找不到圖表：${charts[i].id}`);
      continue;
    }

    const src =
      'imgURI' in result
        ? result.imgURI
        : URL.createObjectURL(result.blob);

    const img = new Image();
    img.src = src;
    img.className = 'chart-img';

    const container = win.document.getElementById(`print-chart-${i}`);

    container?.appendChild(img);

    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });
  }
  // 3) 等圖片載完再 print，避免空白頁/漏圖
  await new Promise<void>((resolve) => setTimeout(() => resolve(), 500));
  win.focus();
  win.print();
}


export type Cell = {
  value?: string | number | boolean | Date;
  merge?: { row?: number; column?: number };
  style?: CellStyle;
};

export type Row = {
  cells: Cell[];
  level?: number;
};

export type StatisticProps = {
  cells: Row[];
};

export function generateSheet(
  cells: Row[],
  defaultStyle: CellStyle = {},
  colInfos: ColInfo[] = []
): WorkSheet {
  const { rows, rowInfos, merges } = cells.reduce(
    (sheet, s, rowIdx) => {
      const columns = s.cells.reduce((cols, cell) => {
        const cellObjs = cellToCellObject(cell, defaultStyle);
        cols.push(cellObjs);
        if (cell.merge) {
          sheet.merges.push({
            s: { r: rowIdx, c: cols.length - 1 },
            e: {
              r: rowIdx + (cell.merge.row ?? 1) - 1,
              c: cols.length - 1 + (cell.merge.column ?? 1) - 1,
            },
          });
          if (cell.merge.column) {
            cols.push(
              ...Array.from({ length: cell.merge.column - 1 }).map((v) =>
                emptyCell(cell.style, defaultStyle)
              )
            );
          }
        }
        return cols;
      }, [] as CellObject[]);
      sheet.rows.push(columns);
      sheet.rowInfos.push({ level: s.level });
      return sheet;
    },
    { rows: [], rowInfos: [], merges: [], rowMergeStack: {} } as {
      rows: CellObject[][];
      rowInfos: RowInfo[];
      merges: Range[];
      rowMergeStack: Record<number, Cell[]>;
    }
  );

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!merges'] = merges;
  sheet['!rows'] = rowInfos;
  sheet['!cols'] = colInfos;

  return sheet;
}

export function downloadSheets(
  fileName: string,
  exportfile: number | undefined, // 0 = xlsx, 1 = ods
  ...sheets: { name: string; sheet: WorkSheet }[]
): void {
  const isODS = exportfile === 1;

  const sanitizeWorksheetForODS = (ws: WorkSheet): WorkSheet => {
    if ((ws as any)['!rows']) delete (ws as any)['!rows'];

    Object.keys(ws).forEach((addr) => {
      if (addr[0] === '!') return;
      const cell: any = (ws as any)[addr];
      if (!cell || typeof cell !== 'object') return;

      if (cell.s) delete cell.s;

      if (typeof cell.f === 'string' && /^HYPERLINK\s*\(/i.test(cell.f)) {
        const m = cell.f.match(/^HYPERLINK\s*\(\s*(.+?)\s*,\s*(.+?)\s*\)\s*$/i);
        if (m) {
          const unq = (s: string) => s.trim().replace(/^"(.*)"$/, '$1');
          const Target = unq(m[1]);
          const Text = unq(m[2]);
          cell.l = { Target };
          if (!cell.v) cell.v = Text;
          delete cell.f;
        }
      }
    });

    return ws;
  };

  const book = XLSX.utils.book_new();
  sheets.forEach(({ sheet, name }) => {
    const ws = isODS ? sanitizeWorksheetForODS(sheet) : sheet;
    XLSX.utils.book_append_sheet(book, ws, name);
  });

  const bookType = isODS ? 'ods' : 'xlsx';
  const mime = isODS
    ? 'application/vnd.oasis.opendocument.spreadsheet'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const ext = isODS ? 'ods' : 'xlsx';

  const buffer = XLSX.write(book, {
    type: 'array',
    bookType,
    cellStyles: false,
  });

  const blob = new Blob([buffer], { type: mime });
  downloadBlob(blob, `${fileName}.${ext}`);
}


export function downloadNameListSheets(
  fileName: string,
  sheets: {
    name: string;
    sheet: WorkSheet;
    styles?: { [cell: string]: XLSX.CellObject };
  }[],
  exportFileType: number = 0
): void {
  const book = XLSX.utils.book_new();

  sheets.forEach(({ sheet, name }) => {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');

    if (!sheet['!merges']) {
      sheet['!merges'] = [];
    }

    sheet['!merges'].push({
      s: { r: 1, c: range.s.c },
      e: { r: 1, c: range.e.c },
    });

    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = sheet[cellAddress] || {};

        if (!cell.s) cell.s = {};

        cell.s.border = {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        };

        if (row === range.s.r) {
          cell.s.border.top = { style: 'double', color: { rgb: '000000' } };
        }

        if (row === range.e.r) {
          cell.s.border.bottom = { style: 'double', color: { rgb: '000000' } };
        }

        if (col === range.s.c) {
          cell.s.border.left = { style: 'double', color: { rgb: '000000' } };
        }

        if (col === range.e.c) {
          cell.s.border.right = { style: 'double', color: { rgb: '000000' } };
        }

        sheet[cellAddress] = cell;
      }
    }

    XLSX.utils.book_append_sheet(book, sheet, name);
  });

  // ⭐ 判斷格式
  const bookType = exportFileType === 1 ? 'ods' : 'xlsx';

  const buffer = XLSX.write(book, {
    type: 'array',
    bookType,
    cellStyles: true,
  });

  const blob = new Blob([buffer], {
    type: 'application/octet-stream',
  });

  downloadBlob(blob, `${fileName}.${bookType}`);
}

function emptyCell(
  style: CellStyle | undefined,
  defaultStyle: CellStyle = {}
): CellObject {
  return { t: 's', s: { ...defaultStyle, ...style }, v: '' };
}

function cellToCellObject(
  cell: Cell,
  defaultStyle: CellStyle = {}
): CellObject {
  if (!cell || cell.value === undefined)
    return emptyCell(cell.style, defaultStyle);
  return {
    s: { ...defaultStyle, ...cell.style },
    t: cellValueType(cell.value),
    v: cell.value,
  };
}

enum CellTypeEnum {
  string = 's',
  number = 'n',
  bool = 'b',
  date = 'd',
}

function cellValueType(value: string | number | boolean | Date): ExcelDataType {
  if (typeof value === 'string') {
    return CellTypeEnum.string;
  }
  if (typeof value === 'boolean') {
    return CellTypeEnum.bool;
  }
  if (typeof value === 'number') {
    return CellTypeEnum.number;
  }
  if (typeof value === 'object' && value instanceof Date) {
    return CellTypeEnum.date;
  }
  return CellTypeEnum.string;
}
